import { POLL_OPTION_MAX_LENGTH, visibilityTypeValues, } from '../types/index.js';
import { canModerateJam } from './jamHelpers.js';
import { defaultRsvpRecurrenceId, effectiveEventTimes, isRecurringEvent, listRsvpOccurrences, normalizeRecurrenceId, sameRecurrenceId, } from './eventRecurrence.js';
import { formatEventWhen } from './eventTime.js';
import { checkGroupCapabilities, checkPostCapabilities, checkRoleCapabilities, } from './capabilitiesHelpers.js';
import { scopeMatches } from './scopeHelpers.js';
import { countBy, dateSorter, groupBy, transformValues } from './utils.js';
const buildThread = (rootPost, postList) => ({
    ...rootPost,
    children: postList
        .filter((p) => p.inReplyToId === rootPost.id)
        .map((post) => buildThread(post, postList)),
});
export const buildThreads = (posts) => posts
    .filter((p) => !posts.find((p2) => p2.id === p.inReplyToId))
    .map((post) => buildThread(post, posts))
    .sort(dateSorter());
const parentIdOf = (post) => post.inReplyToId ?? post.replyTo?.id;
export const buildThreadPreview = (rootId, replies) => {
    const sorted = [...replies].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    if (!sorted.length)
        return [];
    const ids = new Set(sorted.map((post) => post.id));
    const parent = new Map(sorted.map((post) => [post.id, post.id]));
    const find = (id) => {
        const current = parent.get(id) ?? id;
        if (current !== id) {
            const root = find(current);
            parent.set(id, root);
            return root;
        }
        return id;
    };
    const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb)
            parent.set(ra, rb);
    };
    for (const post of sorted) {
        const parentId = parentIdOf(post);
        if (parentId && ids.has(parentId))
            union(post.id, parentId);
    }
    const grouped = new Map();
    for (const post of sorted) {
        const key = find(post.id);
        const list = grouped.get(key) ?? [];
        list.push(post);
        grouped.set(key, list);
    }
    return [...grouped.values()]
        .map((posts) => {
        const start = posts.find((post) => {
            const parentId = parentIdOf(post);
            return !parentId || !ids.has(parentId);
        }) ?? posts[0];
        const ordered = [];
        const used = new Set();
        let current = start;
        while (current && !used.has(current.id)) {
            ordered.push(current);
            used.add(current.id);
            current = posts.find((post) => parentIdOf(post) === current?.id && !used.has(post.id));
        }
        for (const post of posts) {
            if (!used.has(post.id))
                ordered.push(post);
        }
        const startParentId = parentIdOf(start);
        const ancestor = startParentId && startParentId !== rootId
            ? (start.replyTo ?? undefined)
            : undefined;
        const skippedAncestor = !!(ancestor &&
            ancestor.inReplyToId &&
            ancestor.inReplyToId !== rootId);
        return { skippedAncestor, ancestor, posts: ordered };
    })
        .sort((a, b) => {
        const aKey = a.ancestor?.createdAt ?? a.posts[0]?.createdAt ?? '';
        const bKey = b.ancestor?.createdAt ?? b.posts[0]?.createdAt ?? '';
        return aKey.localeCompare(bKey);
    });
};
export const getReactionCount = (post) => {
    return countBy(post.reactions, (r) => r.reaction);
};
export const countVotes = (optionsCount, answers) => {
    const voteCounts = new Array(optionsCount).fill(0);
    for (const answer of answers) {
        for (const index of answer.selection) {
            if (index < optionsCount) {
                voteCounts[index] = voteCounts[index] + 1;
            }
        }
    }
    return voteCounts;
};
/** Fill blank poll choices with their placeholder label (e.g. "Option 1"). */
export const resolvePollOptionContents = (options, fallbackLabel) => options.map((text, index) => {
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : fallbackLabel(index);
});
/** Longer choices are rejected by `questionSchema`, so block submit instead. */
export const pollOptionsWithinLimit = (options) => options.every((text) => text.trim().length <= POLL_OPTION_MAX_LENGTH);
export const collectVotes = (post) => {
    if (post.type !== 'question') {
        return {
            voteCounts: [],
            votes: [],
        };
    }
    const postData = post.data;
    const sortedVoteEntries = post.entries
        .filter((e) => e.type === 'answer')
        .sort(dateSorter());
    const votesByProfile = groupBy(sortedVoteEntries, (e) => e.profile.id);
    const lastVoteByProfile = transformValues(votesByProfile, (votes) => votes.slice(-1)[0]);
    const votes = [...Object.values(lastVoteByProfile)]
        .filter((ve) => ve.data.selection.length > 0)
        .map((entry) => ({
        profile: entry.profile,
        selection: entry.data.selection,
    }));
    const voteCounts = countVotes(postData.options.length, votes);
    return {
        votes,
        voteCounts,
    };
};
const latestRsvpPerProfile = (rsvps, recurrenceId) => {
    const scoped = rsvps.filter((rsvp) => {
        if (recurrenceId) {
            if (rsvp.recurrenceId) {
                return sameRecurrenceId(rsvp.recurrenceId, recurrenceId);
            }
            return true;
        }
        return true;
    });
    const rsvpsByProfile = groupBy(scoped, (r) => r.profile.id);
    return Object.values(rsvpsByProfile).map((profileRsvps) => {
        const instanceRsvps = recurrenceId
            ? profileRsvps.filter((rsvp) => sameRecurrenceId(rsvp.recurrenceId, recurrenceId))
            : [];
        const seriesRsvps = recurrenceId
            ? profileRsvps.filter((rsvp) => !rsvp.recurrenceId)
            : profileRsvps;
        const pool = recurrenceId && instanceRsvps.length > 0 ? instanceRsvps : seriesRsvps;
        const sorted = pool.sort(dateSorter());
        return sorted[sorted.length - 1];
    });
};
export const calculateEffectiveRsvps = (post, recurrenceId) => latestRsvpPerProfile(post.rsvps || [], recurrenceId).filter((rsvp) => !!rsvp);
export const getEffectiveRsvp = (post, profileId, recurrenceId) => calculateEffectiveRsvps(post, recurrenceId).find((r) => r.profile.id === profileId);
export const countYesRsvps = (post, recurrenceId) => calculateEffectiveRsvps(post, recurrenceId).filter((r) => r.response === 'yes').length;
export const countSeriesYesRsvps = (post) => (post.rsvps ?? []).filter((r) => !r.recurrenceId && r.response === 'yes')
    .length;
const attendingResponse = (response) => response === 'yes' || response === 'tentative';
/** Current roster: attending guests, plus organizer removals that can be restored. */
export const listedRsvps = (rsvps) => rsvps.filter((rsvp) => rsvp.response === 'yes' ||
    rsvp.response === 'tentative' ||
    rsvp.response === 'removed');
export const isRsvpCancelNotice = (data) => {
    if (!data || typeof data !== 'object')
        return false;
    const notice = data;
    return (typeof notice.series === 'boolean' &&
        Array.isArray(notice.occurrenceIds) &&
        notice.occurrenceIds.every((id) => typeof id === 'string'));
};
export const rsvpCancelNotice = (post, profileId, response, occurrenceIds) => {
    if (response !== 'no')
        return undefined;
    const event = post.data?.type === 'event' ? post.data : undefined;
    if (!event)
        return undefined;
    if (occurrenceIds?.length) {
        const canceled = occurrenceIds.filter((id) => attendingResponse(getEffectiveRsvp(post, profileId, id)?.response));
        if (!canceled.length)
            return undefined;
        return { occurrenceIds: canceled, series: false };
    }
    if (attendingResponse(getEffectiveRsvp(post, profileId)?.response)) {
        return { occurrenceIds: [], series: isRecurringEvent(event) };
    }
    if (!event.recurrence)
        return undefined;
    const canceled = instanceRsvpIdsForProfile(post, profileId).filter((id) => attendingResponse(getEffectiveRsvp(post, profileId, id)?.response));
    if (!canceled.length)
        return undefined;
    return { occurrenceIds: canceled, series: false };
};
const instanceRsvpKey = (profileId, recurrenceId) => `${profileId}|${normalizeRecurrenceId(recurrenceId)}`;
/** Attending → no transitions, newest first. Later re-RSVPs do not erase them. */
export const listRsvpCancellations = (post) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    const recurring = event ? isRecurringEvent(event) : false;
    const seriesResponse = new Map();
    const instanceResponse = new Map();
    const records = [];
    for (const rsvp of [...(post.rsvps ?? [])].sort(dateSorter())) {
        const profileId = rsvp.profile.id;
        if (rsvp.response === 'no') {
            if (rsvp.recurrenceId) {
                const key = instanceRsvpKey(profileId, rsvp.recurrenceId);
                const previous = instanceResponse.has(key)
                    ? instanceResponse.get(key)
                    : seriesResponse.get(profileId);
                if (attendingResponse(previous)) {
                    records.push({
                        profile: rsvp.profile,
                        recurrenceId: rsvp.recurrenceId,
                        canceledAt: rsvp.createdAt,
                        series: false,
                    });
                }
            }
            else if (attendingResponse(seriesResponse.get(profileId))) {
                records.push({
                    profile: rsvp.profile,
                    canceledAt: rsvp.createdAt,
                    series: recurring,
                });
            }
        }
        if (rsvp.recurrenceId) {
            instanceResponse.set(instanceRsvpKey(profileId, rsvp.recurrenceId), rsvp.response);
        }
        else {
            seriesResponse.set(profileId, rsvp.response);
        }
    }
    return records.reverse();
};
export const rsvpCancelWhenLabels = (post, notice) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    if (!event || notice.series)
        return { series: notice.series, labels: [] };
    const format = (start, end) => formatEventWhen(start, {
        end,
        timeZone: event.timeZone,
        allDay: event.wholeDay,
    });
    if (!notice.occurrenceIds.length) {
        return { series: false, labels: [format(event.start, event.end)] };
    }
    return {
        series: false,
        labels: notice.occurrenceIds.map((id) => {
            const times = effectiveEventTimes(event, id);
            return format(times.start, times.end);
        }),
    };
};
export const instanceRsvpIdsForProfile = (post, profileId) => {
    const ids = new Set();
    for (const rsvp of post.rsvps ?? []) {
        if (rsvp.profile.id !== profileId || !rsvp.recurrenceId)
            continue;
        ids.add(normalizeRecurrenceId(rsvp.recurrenceId));
    }
    return [...ids];
};
export const overlaySeriesRsvpEntries = (response, instanceIds) => [
    { response },
    ...instanceIds.map((recurrenceId) => ({ response, recurrenceId })),
];
export const seriesYesBlockedByCapacity = (post, profileId, now = new Date()) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    const maxAttendees = event?.maxAttendees;
    if (!event || !maxAttendees || !event.recurrence)
        return false;
    const occurrences = listRsvpOccurrences(event, now);
    // No open date left (the current one has ended and nothing follows).
    if (occurrences.length === 0) {
        const current = getEffectiveRsvp(post, profileId);
        if (current?.response !== 'yes' &&
            countSeriesYesRsvps(post) >= maxAttendees) {
            return true;
        }
        return false;
    }
    return occurrences.some((occurrence) => {
        const current = getEffectiveRsvp(post, profileId, occurrence.recurrenceId);
        if (current?.response === 'yes')
            return false;
        return countYesRsvps(post, occurrence.recurrenceId) >= maxAttendees;
    });
};
export const occurrenceHasCapacity = (post, recurrenceId, profileId) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    if (!event?.maxAttendees)
        return true;
    if (profileId) {
        const current = getEffectiveRsvp(post, profileId, recurrenceId);
        if (current?.response === 'yes')
            return true;
    }
    return countYesRsvps(post, recurrenceId) < event.maxAttendees;
};
/** Series Register stays enabled while any upcoming date still has a seat. */
export const recurringEventHasOpenOccurrence = (post, profileId, now = new Date()) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    if (!event)
        return false;
    if (!event.recurrence) {
        return occurrenceHasCapacity(post, undefined, profileId);
    }
    return listRsvpOccurrences(event, now).some((occurrence) => occurrenceHasCapacity(post, occurrence.recurrenceId, profileId));
};
/**
 * Series-page RSVP for the Register/Cancel CTA: prefer the default/next date,
 * then any attending instance so a partial RSVP still shows Cancel.
 */
export const displayRsvpForProfile = (post, profileId, options) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    // One-time events have no occurrences. Older claims stamped the start time
    // onto some RSVPs; a newer unscoped waitlist must still be the current one.
    if (event && !event.recurrence) {
        return getEffectiveRsvp(post, profileId);
    }
    const scopedId = options?.lockToOccurrence
        ? options.recurrenceId
        : event
            ? defaultRsvpRecurrenceId(event, options?.recurrenceId, options?.now)
            : options?.recurrenceId;
    const scoped = getEffectiveRsvp(post, profileId, scopedId);
    if (options?.lockToOccurrence)
        return scoped;
    if (scoped && scoped.response !== 'no')
        return scoped;
    if (event?.recurrence) {
        for (const occurrence of listRsvpOccurrences(event, options?.now)) {
            const instance = getEffectiveRsvp(post, profileId, occurrence.recurrenceId);
            if (instance && instance.response !== 'no')
                return instance;
        }
    }
    return scoped;
};
export const isCapacityEvent = (event) => !!event.maxAttendees;
export const DEFAULT_WAITLIST_OFFER_MINUTES = 60;
export const MAX_WAITLIST_OFFER_MINUTES = 7 * 24 * 60;
export const parseEventMaxAttendeesInput = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};
export const normalizeWaitlistOfferMinutes = (value) => {
    if (value == null || !Number.isFinite(value))
        return undefined;
    const minutes = Math.trunc(value);
    if (minutes < 1 || minutes > MAX_WAITLIST_OFFER_MINUTES)
        return undefined;
    return minutes;
};
export const parseEventWaitlistOfferMinutesInput = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    return normalizeWaitlistOfferMinutes(Number.parseInt(trimmed, 10));
};
export const withoutEventMaxAttendees = (event) => {
    const { maxAttendees: _, waitlistOfferMinutes: __, ...rest } = event;
    return rest;
};
export const normalizeEventDataForSave = (event) => {
    const { maxAttendees } = event;
    if (maxAttendees == null ||
        !Number.isFinite(maxAttendees) ||
        maxAttendees < 1) {
        return withoutEventMaxAttendees(event);
    }
    const minutes = normalizeWaitlistOfferMinutes(event.waitlistOfferMinutes);
    const { waitlistOfferMinutes: _, ...rest } = event;
    return {
        ...rest,
        maxAttendees: Math.trunc(maxAttendees),
        ...(minutes != null ? { waitlistOfferMinutes: minutes } : {}),
    };
};
/** Null capacity means it was cleared; strip for validation/output. */
export const normalizeEventDataFromDb = (event) => {
    const { maxAttendees, waitlistOfferMinutes, ...rest } = event;
    return {
        ...rest,
        ...(maxAttendees != null ? { maxAttendees } : {}),
        ...(waitlistOfferMinutes != null ? { waitlistOfferMinutes } : {}),
    };
};
export const normalizePostDataFromDb = (data) => data.type === 'event' ? normalizeEventDataFromDb(data) : data;
export const eventDataForDbUpdate = (previous, normalized) => {
    let next = normalized;
    if (previous?.maxAttendees != null && !('maxAttendees' in normalized)) {
        next = { ...next, maxAttendees: null };
    }
    if (previous?.waitlistOfferMinutes != null &&
        !('waitlistOfferMinutes' in normalized)) {
        next = { ...next, waitlistOfferMinutes: null };
    }
    return next;
};
export const canManageEventRsvps = (profile, post) => !!(profile &&
    (post.profile.id === profile.id || canModerateJam(profile, post)));
export const getJamCapacityJoinBlock = (post, profile, recurrenceId) => {
    const event = post.data?.type === 'event' ? post.data : undefined;
    if (!event?.maxAttendees || !profile?.id) {
        return { blocked: false };
    }
    if (canModerateJam(profile, post)) {
        return { blocked: false };
    }
    const myRsvp = getEffectiveRsvp(post, profile.id, recurrenceId);
    if (myRsvp?.response === 'yes') {
        return { blocked: false };
    }
    if (myRsvp?.response === 'removed') {
        return { blocked: true, reason: 'removed' };
    }
    if (countYesRsvps(post, recurrenceId) >= event.maxAttendees) {
        return { blocked: true, reason: 'full' };
    }
    // A freed seat stays reserved for the waitlist unless this profile holds the offer.
    const waiting = calculateEffectiveRsvps(post, recurrenceId).filter((rsvp) => rsvp.response === 'waitlist' && rsvp.profile.type !== 'federated');
    const mine = waiting.find((rsvp) => rsvp.profile.id === profile.id);
    const offerOpen = !!mine?.offerExpiresAt && Date.parse(mine.offerExpiresAt) > Date.now();
    if (waiting.length > 0 && !offerOpen) {
        return { blocked: true, reason: 'full' };
    }
    return { blocked: true, reason: 'rsvp-required' };
};
export const canDeletePost = (authData, post, config) => checkPostCapabilities(authData, ['core-posts-delete'], post, config).success;
export const canCreatePost = (authData, type, visibility, group) => visibility === 'group'
    ? !!group &&
        checkGroupCapabilities(authData, [`core-posts-create-${type}`], group)
            .success
    : checkRoleCapabilities(authData.profile?.roles, [
        `core-posts-create-${type}-${visibility}`,
    ]).success &&
        scopeMatches({
            scopes: authData.scopes,
            requiredScope: { scopeLevel: 'write', resource: { type: 'posts' } },
        });
export const canCreatePostTypeInAnyGroup = (authData, type) => scopeMatches({
    scopes: authData.scopes,
    requiredScope: { scopeLevel: 'write', resource: { type: 'groups' } },
}) &&
    !!authData.profile?.memberships?.some((m) => checkGroupCapabilities(authData, [`core-posts-create-${type}`], m.group).success);
export const canCreatePostTypeWithVisibility = (authData, type, visibility) => visibility === 'group'
    ? canCreatePostTypeInAnyGroup(authData, type)
    : checkRoleCapabilities(authData.profile?.roles, [
        `core-posts-create-${type}-${visibility}`,
    ]).success &&
        scopeMatches({
            scopes: authData.scopes,
            requiredScope: { scopeLevel: 'write', resource: { type: 'posts' } },
        });
export const canCreatePostType = (authData, type) => visibilityTypeValues.some((visibility) => canCreatePostTypeWithVisibility(authData, type, visibility));
/**
 * Whether Reply / Repost / Like should look live. Non-members of a group
 * post stay fully disabled (existing feed rule). Each action also follows
 * the capability the matching write endpoint actually checks: feed replies
 * and reposts need create-*, likes need core-posts-react.
 */
export const getPostActionAvailability = (authData, post, config) => {
    const isGroupPost = !!post.groupId || !!post.group;
    const membershipExists = !!authData.profile?.memberships?.some((m) => m.group.id === post.group?.id || m.group.id === post.groupId);
    if (isGroupPost && !membershipExists) {
        return { canReply: false, canRepost: false, canReact: false };
    }
    const canReply = checkPostCapabilities(authData, ['core-posts-reply'], post, config)
        .success &&
        canCreatePost(authData, 'note', post.visibility, post.group ?? undefined);
    const canReact = checkPostCapabilities(authData, ['core-posts-react'], post, config).success;
    const canRepost = post.group
        ? checkGroupCapabilities(authData, [`core-posts-create-${post.type}`], post.group).success
        : canCreatePost(authData, post.type, post.visibility);
    return { canReply, canRepost, canReact };
};
/** Sentinel handle for block-hidden reply tombstones (no name/handle leak). */
export const HIDDEN_AUTHOR_HANDLE = 'hidden';
export const toHiddenPost = (post) => ({
    ...post,
    hidden: true,
    data: {
        ...post.data,
        ...('content' in post.data ? { content: '' } : {}),
    },
    profile: {
        ...post.profile,
        displayName: undefined,
        handle: HIDDEN_AUTHOR_HANDLE,
        avatar: null,
        header: null,
        bio: undefined,
    },
});
export const isHiddenPost = (post) => !!post.hidden;
export const participantIds = (post) => {
    const ids = new Set((post.audience ?? []).map((member) => member.id).filter(Boolean));
    const creatorId = post.creatorId ?? post.profile?.id;
    if (creatorId)
        ids.add(creatorId);
    return ids;
};
/** Direct message between exactly two participants. */
export const isOneToOneDm = (post) => post.visibility === 'direct' && participantIds(post).size === 2;
export const isOneToOneWithBlocked = (post, viewer, blockedIds) => {
    if (!viewer || post.visibility !== 'direct')
        return false;
    const blocked = blockedIds instanceof Set ? blockedIds : new Set(blockedIds ?? []);
    if (!blocked.size)
        return false;
    const others = participantIds(post);
    others.delete(viewer.id);
    return others.size === 1 && blocked.has([...others][0]);
};
